import React from 'react';

import { useSetAtom } from '@/core/features/store/atom-store';
import { deepEquals } from '@/core/utils/object';
import { useGoogleMapInstance } from '@/features/map/google-map/google-map-state';
import { logMap } from '@/features/map/map-container/map-logger';
import { useSetMapState } from '@/features/map/map-container/map-state';
import { getBoundariesObject, getMapLatLngLiteral } from '@/features/map/map-data-v5/map-data-v5-service';
import { setMapBoundariesAtom } from '@/features/map/map-data-v5/use-map-data-v5';

export function useMapMovements() {
    const googleMapInstance = useGoogleMapInstance();
    const setMapBoundaries = useSetAtom(setMapBoundariesAtom);
    const setMapState = useSetMapState();

    const panTo = React.useCallback(
        (location?: { latitude: number; longitude: number }, zoom?: number) => {
            if (!googleMapInstance) {
                return;
            }

            const currentLocation = getMapLatLngLiteral(googleMapInstance.getCenter());

            if (!location || deepEquals(location, currentLocation)) {
                return;
            }

            const currentZoom = googleMapInstance.getZoom();

            if (!currentZoom) {
                googleMapInstance.setZoom(Math.max(zoom ?? 2, 2)); // zoom has to be at least 2
                return;
            }

            // TODO: Improvement: we can update the boundary "much" earlier because we know the destination.
            //  We just have to calculate it. No need to wait for the animation to finish

            if (zoom === currentZoom) {
                setMapState('animating');
                googleMapInstance.panTo({ lat: location.latitude, lng: location.longitude });
                google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
                    setTimeout(() => {
                        const boundaries = googleMapInstance.getBounds();

                        if (!boundaries) {
                            return;
                        }

                        setMapBoundaries(getBoundariesObject(boundaries, zoom ?? currentZoom));
                        setMapState('animation_finished');
                    }, 0);
                });

                return;
            }

            // animated zoom
            if (zoom && currentZoom !== zoom) {
                setMapState('animating');
                const isZoomIn = zoom > currentZoom;
                const zoomDiff = zoom !== currentZoom ? Math.abs(zoom - currentZoom) : 0;
                const stepCount = Math.ceil(zoomDiff);
                const stepSize = zoomDiff / stepCount;

                logMap('move map to', { currentZoom, location, zoom, zoomDiff });

                Array.from({ length: stepCount }).forEach((_, index) => {
                    setTimeout(
                        () => {
                            googleMapInstance.setZoom(currentZoom + (index + 1) * (isZoomIn ? stepSize : -stepSize));
                            if (location) {
                                // keep position while zooming
                                googleMapInstance.panTo({ lat: location.latitude, lng: location.longitude });
                            }

                            if (index + 1 === stepCount || stepCount === 1) {
                                google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
                                    setTimeout(() => {
                                        const boundaries = googleMapInstance.getBounds();

                                        if (!boundaries) {
                                            return;
                                        }

                                        setMapBoundaries(getBoundariesObject(boundaries, zoom ?? currentZoom));
                                        setMapState('animation_finished');
                                    }, 0);
                                });
                            }
                        },
                        100 * index + 1,
                    );
                });
            }
        },
        [googleMapInstance, setMapBoundaries, setMapState],
    );

    const jumpTo = React.useCallback(
        (location?: { latitude: number; longitude: number }, zoom?: number) => {
            if (!googleMapInstance) {
                return;
            }

            if (!location && !zoom) {
                return;
            }

            if (zoom) {
                googleMapInstance.setZoom(zoom);
            }

            if (location) {
                googleMapInstance.setCenter({ lat: location.latitude, lng: location.longitude });
            }

            google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
                const boundaries = googleMapInstance.getBounds();
                const zoom = googleMapInstance.getZoom();

                if (!boundaries || !zoom) {
                    return;
                }

                setMapBoundaries(getBoundariesObject(boundaries, zoom));
            });
        },
        [googleMapInstance, setMapBoundaries],
    );

    return { jumpTo, panTo };
}
